import { useNuxtApp } from '#app'
import { getMallAppToken, getToken } from '@/utils/globalData'
import { generateEventId, getMetaBrowserTracking } from '@/utils/eventTracking'
import { fixFbParams, shouldReportFacebookForCurrentShop, shouldReportTikTokForCurrentShop } from "@/api/mall"
import { normalizeTikTokPhoneIdentifier } from '@/utils/tiktokIdentity'
import { normalizeGoogleAdsConversionDestinations } from '@/utils/googleTracking'
import {
  isValidOrderSuccessCode,
  reportWebFunnelEvent,
  reportWebOrderSuccessBlocked,
} from '@/utils/clientEventLog'

/**
 * Analytics utility supporting Google Analytics, TikTok Pixel, and Facebook Pixel
 *
 * Features:
 * - Automatic SDK initialization checking for TikTok and Facebook Pixel
 * - Event queuing with timeout for SDK readiness
 * - User identification support for all platforms
 * - Backward compatibility with Google Analytics
 *
 * Usage Examples:
 *
 * // ViewContent event for all platforms
 * trackEvent('ViewContent', {
 *   contents: [{ content_id: '123', content_name: 'Product Name' }],
 *   value: 99.99,
 *   currency: 'USD'
 * });
 *
 * // AddToCart with user identification
 * trackEvent('AddToCart', {
 *   contents: [{ content_id: '123', content_name: 'Product Name' }],
 *   value: 99.99,
 *   currency: 'USD',
 *   userData: { email: 'hashed_email@example.com' }
 * });
 *
 * // Search event
 * trackEvent('Search', {
 *   contents: [{ content_id: '123', content_name: 'Search Result' }],
 *   value: 0,
 *   currency: 'USD',
 *   search_string: 'red shoes'
 * });
 *
 * // Backward compatibility with Google Analytics
 * trackEvent('ecommerce', {
 *   category: 'ecommerce',
 *   action: 'purchase',
 *   label: 'Product Purchase',
 *   value: 99.99
 * });
 */

// Check if TikTok Pixel SDK is ready
function isTikTokReady() {
  return process.client &&
         window.ttq &&
         typeof window.ttq.track === 'function' &&
         typeof window.ttq.identify === 'function';
}

// Check if Facebook Pixel SDK is ready
function isFacebookReady() {
  return process.client &&
         window.fbq &&
         typeof window.fbq === 'function';
}

// Wait for TikTok SDK to be ready with timeout
function waitForTikTokReady(timeout = 5000) {
  return new Promise((resolve) => {
    if (isTikTokReady()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkReady = () => {
      if (isTikTokReady()) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        console.warn('TikTok SDK initialization timeout');
        resolve(false);
        return;
      }

      setTimeout(checkReady, 100);
    };

    checkReady();
  });
}

// Wait for Facebook Pixel SDK to be ready with timeout
function waitForFacebookReady(timeout = 5000) {
  return new Promise((resolve) => {
    if (isFacebookReady()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkReady = () => {
      if (isFacebookReady()) {
        resolve(true);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        console.warn('Facebook Pixel SDK initialization timeout');
        resolve(false);
        return;
      }

      setTimeout(checkReady, 100);
    };

    checkReady();
  });
}

// TikTok Pixel event tracking
async function trackTikTokEvent(eventName, parameters = {}, options = {}) {
  if (!shouldReportTikTokForCurrentShop()) {
    return;
  }

  try {
    // Wait for TikTok SDK to be ready
    const isReady = await waitForTikTokReady();

    if (!isReady) {
      console.warn('TikTok SDK not ready, skipping event:', eventName);
      return;
    }

    if (options.eventId) {
      window.ttq.track(eventName, parameters, {
        event_id: options.eventId
      });
      return;
    }

    window.ttq.track(eventName, parameters);
  } catch (e) {
    console.warn('TikTok tracking error:', e);
  }
}

// Facebook Pixel event tracking
async function trackFacebookEvent(eventName, parameters = {}, options = {}) {
  if (!shouldReportFacebookForCurrentShop()) {
    return
  }

  try {
    const purchaseContext = buildFacebookPurchaseContext({
      eventName,
      eventId: options.eventId,
      userData: options.userData,
      value: parameters?.value,
      currency: parameters?.currency
    })

    if (purchaseContext.isDuplicate) {
      console.warn('Facebook Pixel Purchase already sent, skipping duplicate:', purchaseContext.dedupKey)
      return
    }

    // Wait for Facebook Pixel SDK to be ready
    const isReady = await waitForFacebookReady();

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping event:', eventName);
      return;
    }

    if (options.eventId) {
      window.fbq('track', eventName, parameters, {
        eventID: options.eventId
      });
      if (eventName === 'Purchase') {
        markFacebookPurchaseEventSent(purchaseContext.dedupKey)
      }
      return;
    }

    window.fbq('track', eventName, parameters);
    if (eventName === 'Purchase') {
      markFacebookPurchaseEventSent(purchaseContext.dedupKey)
    }
  } catch (e) {
    console.warn('Facebook tracking error:', e);
  }
}

// Facebook Pixel custom event tracking
async function trackFacebookCustomEvent(eventName, parameters = {}, options = {}) {
  if (!shouldReportFacebookForCurrentShop()) {
    return
  }

  try {
    const isReady = await waitForFacebookReady()

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping custom event:', eventName)
      return
    }

    if (options.eventId) {
      window.fbq('trackCustom', eventName, parameters, {
        eventID: options.eventId,
      })
      return
    }

    window.fbq('trackCustom', eventName, parameters)
  } catch (e) {
    console.warn('Facebook custom event tracking error:', e)
  }
}

// TikTok user identification
async function identifyTikTokUser(userData = {}) {
  if (!shouldReportTikTokForCurrentShop()) {
    return;
  }

  try {
    // Wait for TikTok SDK to be ready
    const isReady = await waitForTikTokReady();

    if (!isReady) {
      console.warn('TikTok SDK not ready, skipping user identification');
      return;
    }

    const identifyData = {};

    if (userData.email) {
      identifyData.email = userData.email; // Should be SHA-256 hashed
    }
    // TikTok validates the original phone format before hashing it.
    const phoneNumber = normalizeTikTokPhoneIdentifier(
      userData.phone_number,
      userData.country_code || userData.country,
    )
    if (phoneNumber) {
      identifyData.phone_number = phoneNumber;
    }
    if (userData.external_id) {
      identifyData.external_id = userData.external_id; // Should be SHA-256 hashed
    }

    if (Object.keys(identifyData).length > 0) {
      window.ttq.identify(identifyData);
    }
  } catch (e) {
    console.warn('TikTok identify error:', e);
  }
}

// Facebook user identification
async function identifyFacebookUser(userData = {}) {
  if (!shouldReportFacebookForCurrentShop()) {
    return
  }

  try {
    // Wait for Facebook Pixel SDK to be ready
    const isReady = await waitForFacebookReady();

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping user identification');
      return;
    }

    const identifyData = {};

    if (userData.email) {
      identifyData.em = userData.email; // Should be SHA-256 hashed
    }
    if (userData.phone_number) {
      identifyData.ph = userData.phone_number; // Should be SHA-256 hashed
    }
    if (userData.first_name) {
      identifyData.fn = userData.first_name; // Should be SHA-256 hashed
    }
    if (userData.last_name) {
      identifyData.ln = userData.last_name; // Should be SHA-256 hashed
    }
    if (userData.external_id) {
      identifyData.external_id = userData.external_id; // Should be SHA-256 hashed
    }

    if (Object.keys(identifyData).length > 0) {
      // Facebook Pixel uses userData in event parameters or setUserData
      window.fbq('set', 'userData', identifyData);
    }
  } catch (e) {
    console.warn('Facebook identify error:', e);
  }
}

// Google Analytics event tracking (backward compatibility)
function trackGoogleAnalytics(category, action, label = "", value = 0) {
  try {
    if (process.client && typeof window.gtag === 'function') {
      const analyticsTagIds = getConfiguredGoogleTagIds()
        .filter(id => /^(?:G-[A-Z0-9]+|UA-\d+-\d+)$/i.test(id))
      if (analyticsTagIds.length === 0) {
        return false
      }

      window.gtag("event", action, {
        send_to: analyticsTagIds.length === 1 ? analyticsTagIds[0] : analyticsTagIds,
        event_category: category,
        event_label: label,
        value: value,
      });
      return true
    }
  } catch (e) {
    return false
  }

  return false
}

function getConfiguredGoogleTagIds() {
  if (!process.client || !Array.isArray(window.__configuredGoogleTagIds)) {
    return []
  }

  return [...new Set(window.__configuredGoogleTagIds
    .map(id => String(id || '').trim())
    .filter(Boolean))]
}

function getConfiguredGoogleAdsTagIds() {
  return getConfiguredGoogleTagIds()
    .filter(id => /^AW-\d+$/i.test(id))
}

const googleAdsMarketingEventMap = {
  ViewContent: 'view_item',
  AddToCart: 'add_to_cart',
  ViewCart: 'view_cart',
  InitiateCheckout: 'begin_checkout',
  AddPaymentInfo: 'add_payment_info',
  Search: 'view_search_results',
  CompleteRegistration: 'sign_up',
  Purchase: 'purchase',
}

function buildGoogleAdsRetailItems(contents = []) {
  return contents
    .map((content) => {
      const id = content?.content_id || content?.item_id || content?.id
      if (id === undefined || id === null || String(id).trim() === '') {
        return null
      }

      const item = {
        id: String(id),
        google_business_vertical: 'retail',
      }
      const price = Number(content?.price)
      const quantity = Number(content?.quantity)
      if (Number.isFinite(price) && price >= 0) {
        item.price = price
      }
      if (Number.isFinite(quantity) && quantity > 0) {
        item.quantity = quantity
      }
      return item
    })
    .filter(Boolean)
}

function trackGoogleAdsMarketingEvent(eventType, options = {}) {
  try {
    if (!process.client || typeof window.gtag !== 'function') {
      return false
    }

    const eventName = googleAdsMarketingEventMap[eventType]
    const tagIds = getConfiguredGoogleAdsTagIds()
    if (!eventName || tagIds.length === 0) {
      return false
    }

    const {
      contents = [],
      value,
      currency,
      searchString,
      transactionId,
    } = options
    const items = buildGoogleAdsRetailItems(contents)
    const numericValue = Number(value)
    const params = {
      send_to: tagIds.length === 1 ? tagIds[0] : tagIds,
    }

    if (items.length > 0) {
      params.items = items
    }
    if (Number.isFinite(numericValue) && numericValue >= 0) {
      params.value = numericValue
      if (currency) {
        params.currency = currency
      }
    }
    if (eventName === 'view_search_results' && searchString) {
      params.search_term = searchString
    }
    if (eventName === 'purchase' && transactionId) {
      params.transaction_id = transactionId
    }

    window.gtag('event', eventName, params)
    return true
  }
  catch (e) {
    return false
  }
}

export function trackGoogleAdsEvent(eventType, options = {}) {
  return trackGoogleAdsMarketingEvent(eventType, options)
}

function trackGoogleAdsConversion(sendTo, { value, currency, transactionId } = {}) {
  try {
    if (!process.client || typeof window.gtag !== 'function') {
      return false
    }

    const destinations = normalizeGoogleAdsConversionDestinations(sendTo)
    if (destinations.length === 0) {
      return false
    }

    window.gtag('event', 'conversion', {
      send_to: destinations.length === 1 ? destinations[0] : destinations,
      value: normalizeValue(value),
      currency,
      transaction_id: transactionId,
    })
    return true
  }
  catch (e) {
    return false
  }
}

/**
 * Clean and normalize value to ensure it's a valid number
 * TikTok requires value to be a number (integer or decimal) without currency symbols, letters, special characters, or commas
 * @param {any} value - The value to clean (can be string, number, etc.)
 * @returns {number} - A valid number (defaults to 0 if invalid)
 */
function normalizeValue(value) {
  // If value is already a valid number, return it
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }

  // If value is undefined, null, or empty, return 0
  if (value === undefined || value === null || value === '') {
    return 0;
  }

  // Convert to string and remove all non-numeric characters except decimal point
  // This removes currency symbols, commas, letters, and other special characters
  let cleanedValue = String(value)
    .replace(/,/g, '') // Remove commas (thousand separators) first
    .replace(/[^\d.]/g, '') // Remove everything except digits and decimal point
    .replace(/\.{2,}/g, '.') // Replace multiple decimal points with single decimal point
    .replace(/^\./, '0.') // Add leading zero if starts with decimal point
    .replace(/\.$/, '') // Remove trailing decimal point
    || '0'; // Default to '0' if empty after cleaning

  // Convert to number
  const numValue = parseFloat(cleanedValue);

  // Return valid number or 0 as fallback
  return isNaN(numValue) || !isFinite(numValue) ? 0 : numValue;
}

function normalizePlatformProperties(properties = {}) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return {}
  }

  return Object.entries(properties).reduce((result, [key, value]) => {
    if (value === undefined || value === null || value === '') {
      return result
    }

    result[key] = value
    return result
  }, {})
}

/**
 * Track a browser-only custom event with TikTok Pixel, Meta Pixel, and GA.
 * Meta reporting continues to follow the current shop's ads-only policy.
 */
export function trackCustomEvent(eventName, options = {}) {
  const normalizedEventName = typeof eventName === 'string' ? eventName.trim() : ''
  if (!normalizedEventName) {
    return { eventId: undefined }
  }

  const {
    parameters = {},
    eventId,
    category,
    action,
    label,
    value,
  } = options
  const normalizedParameters = normalizePlatformProperties(parameters)

  void trackTikTokEvent(normalizedEventName, normalizedParameters, { eventId })
  void trackFacebookCustomEvent(normalizedEventName, normalizedParameters, { eventId })

  if (category && action) {
    trackGoogleAnalytics(category, action, label, value)
  }

  return { eventId }
}

function getCookieValue(key) {
  if (!process.client || !key) {
    return ''
  }

  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${key}=([^;]*)`))
    return match?.[1] ? decodeURIComponent(match[1]) : ''
  } catch (error) {
    return ''
  }
}

function normalizeEventStringValue(value) {
  if (value === undefined || value === null) {
    return ''
  }

  return `${value}`.trim()
}

function resolveFacebookShopId() {
  return getCookieValue('dealerShopId')
}

function resolveFacebookUserId(userData = {}) {
  return normalizeEventStringValue(
    userData.user_id
    || userData.userId
    || userData.external_id
    || userData.externalId
    || resolveFacebookShopId()
  )
}

function buildFacebookEventSourceUrl() {
  if (!process.client || typeof window === 'undefined') {
    return ''
  }

  return fixFbParams(window.location.href)
}

const FACEBOOK_PURCHASE_DEDUP_STORAGE_KEY = 'facebook_purchase_event_dedup'
const FACEBOOK_PURCHASE_DEDUP_MAX_SIZE = 10

function getFacebookPurchaseDedupList() {
  if (!process.client) {
    return []
  }

  try {
    const parsed = JSON.parse(localStorage.getItem(FACEBOOK_PURCHASE_DEDUP_STORAGE_KEY) || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

function buildFacebookPurchaseDedupKey({ eventId, userData, value, currency }) {
  const normalizedEventId = normalizeEventStringValue(eventId)
  const normalizedExternalId = normalizeEventStringValue(userData?.external_id || userData?.externalId || userData?.user_id || userData?.userId)
  const normalizedValue = normalizeEventStringValue(value)
  const normalizedCurrency = normalizeEventStringValue(currency)

  return [normalizedEventId, normalizedExternalId, normalizedValue, normalizedCurrency]
    .filter(Boolean)
    .join('::')
}

function hasSentFacebookPurchaseEvent(dedupKey) {
  if (!dedupKey) {
    return false
  }

  return getFacebookPurchaseDedupList().includes(dedupKey)
}

function markFacebookPurchaseEventSent(dedupKey) {
  if (!process.client || !dedupKey) {
    return
  }

  try {
    const nextList = [dedupKey, ...getFacebookPurchaseDedupList().filter(item => item !== dedupKey)]
      .slice(0, FACEBOOK_PURCHASE_DEDUP_MAX_SIZE)

    localStorage.setItem(FACEBOOK_PURCHASE_DEDUP_STORAGE_KEY, JSON.stringify(nextList))
  } catch (error) {
    // Ignore localStorage failures to avoid blocking checkout success flow.
  }
}

function buildFacebookPurchaseContext({ eventName, eventId, userData, value, currency }) {
  if (eventName !== 'Purchase') {
    return {
      dedupKey: '',
      isDuplicate: false
    }
  }

  const dedupKey = buildFacebookPurchaseDedupKey({ eventId, userData, value, currency })

  return {
    dedupKey,
    isDuplicate: hasSentFacebookPurchaseEvent(dedupKey)
  }
}

async function sha256Hex(value) {
  const normalizedValue = normalizeEventStringValue(value)
  if (!normalizedValue) {
    return ''
  }

  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(normalizedValue)
    const digest = await window.crypto.subtle.digest('SHA-256', data)

    return Array.from(new Uint8Array(digest))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch (error) {
    return normalizedValue
  }
}

function normalizeFacebookServerUserData(userData = {}) {
  if (!userData || typeof userData !== 'object' || Array.isArray(userData)) {
    return undefined
  }

  const normalizedUserData = {}

  if (userData.external_id || userData.externalId) {
    normalizedUserData.externalId = userData.external_id || userData.externalId
  }
  if (userData.email || userData.em) {
    normalizedUserData.email = userData.email || userData.em
  }
  if (userData.phone_number || userData.phone || userData.ph) {
    normalizedUserData.phone = userData.phone_number || userData.phone || userData.ph
  }
  if (userData.city || userData.ct) {
    normalizedUserData.city = userData.city || userData.ct
  }
  if (userData.state || userData.st) {
    normalizedUserData.state = userData.state || userData.st
  }
  if (userData.zip || userData.zp) {
    normalizedUserData.zip = userData.zip || userData.zp
  }
  if (userData.country) {
    normalizedUserData.country = userData.country
  }
  if (userData.client_ip_address || userData.clientIpAddress) {
    normalizedUserData.clientIpAddress = userData.client_ip_address || userData.clientIpAddress
  }
  if (userData.client_user_agent || userData.clientUserAgent) {
    normalizedUserData.clientUserAgent = userData.client_user_agent || userData.clientUserAgent
  }
  if (userData.fbp) {
    normalizedUserData.fbp = userData.fbp
  } else {
    normalizedUserData.fbp = getCookieValue('_fbp')
  }
  if (userData.fbc) {
    normalizedUserData.fbc = userData.fbc
  } else {
    normalizedUserData.fbc = getCookieValue('_fbc')
  }

  return Object.keys(normalizedUserData).length > 0 ? normalizedUserData : undefined
}

async function buildFacebookServerUserData(userData = {}) {
  const normalizedUserData = normalizeFacebookServerUserData(userData) || {}
  const browserTracking = process.client ? getMetaBrowserTracking() : {}
  const payload = {}

  const fbp = normalizeEventStringValue(normalizedUserData.fbp || browserTracking.fbp)
  if (fbp) {
    payload.fbp = fbp
  }

  const fbc = normalizeEventStringValue(normalizedUserData.fbc || browserTracking.fbc)
  if (fbc) {
    payload.fbc = fbc
  }

  const externalId = await sha256Hex(normalizedUserData.externalId)
  if (externalId) {
    payload.externalId = externalId
  }

  const clientIpAddress = normalizeEventStringValue(normalizedUserData.clientIpAddress)
  if (clientIpAddress) {
    payload.clientIpAddress = clientIpAddress
  }

  const clientUserAgent = normalizeEventStringValue(
    normalizedUserData.clientUserAgent || (process.client ? window.navigator.userAgent : '')
  )
  if (clientUserAgent) {
    payload.clientUserAgent = clientUserAgent
  }

  const em = await sha256Hex(normalizedUserData.email && normalizeEventStringValue(normalizedUserData.email).toLowerCase())
  if (em) {
    payload.em = [em]
  }

  const ph = await sha256Hex(normalizedUserData.phone && normalizeEventStringValue(normalizedUserData.phone).replace(/(?!^\+)[^\d]/g, ''))
  if (ph) {
    payload.ph = [ph]
  }

  const ct = await sha256Hex(normalizedUserData.city && normalizeEventStringValue(normalizedUserData.city).toLowerCase())
  if (ct) {
    payload.ct = [ct]
  }

  const st = await sha256Hex(normalizedUserData.state && normalizeEventStringValue(normalizedUserData.state).toLowerCase())
  if (st) {
    payload.st = [st]
  }

  const zp = await sha256Hex(normalizedUserData.zip)
  if (zp) {
    payload.zp = [zp]
  }

  const country = await sha256Hex(normalizedUserData.country && normalizeEventStringValue(normalizedUserData.country).toLowerCase())
  if (country) {
    payload.country = [country]
  }

  return Object.keys(payload).length > 0 ? payload : undefined
}

function buildFacebookServerCustomData(eventType, contents = [], value, currency, facebookProperties = {}) {
  const contentIds = contents
    .map(content => content.content_id || content.id)
    .filter(Boolean)
    .map(item => `${item}`)

  const contentName = facebookProperties.contentName
    || facebookProperties.content_name
    || (contents.length > 0 ? (contents[0].content_name || contents[0].name) : undefined)

  const contentCategory = facebookProperties.contentCategory
    || facebookProperties.content_category
    || (contents.length > 0 ? (contents[0].content_category || contents[0].category) : undefined)

  const customData = {
    value: normalizeValue(value),
    currency,
    contentIds,
    contentType: facebookProperties.contentType || facebookProperties.content_type || 'product',
    contentName,
    contentCategory,
    numItems: facebookProperties.numItems || facebookProperties.num_items || contents.length || undefined,
    ...normalizePlatformProperties(facebookProperties)
  }

  if (eventType === 'CompleteRegistration') {
    delete customData.value
    delete customData.currency
    delete customData.contentIds
    delete customData.contentType
    delete customData.contentName
    delete customData.contentCategory
    delete customData.numItems
  }

  Object.keys(customData).forEach(key => {
    if (customData[key] === undefined) {
      delete customData[key]
    }
  })

  return Object.keys(customData).length > 0 ? customData : undefined
}

async function sendFacebookServerEvent(eventName, {
  eventId,
  contents = [],
  value,
  currency = 'USD',
  userData,
  facebookProperties = {}
} = {}) {
  if (!process.client || !['AddToCart', 'CompleteRegistration', 'Purchase'].includes(eventName)) {
    return
  }

  if (!shouldReportFacebookForCurrentShop()) {
    return
  }

  try {
    const isReady = await waitForFacebookReady()

    if (!isReady) {
      console.warn('Facebook Pixel SDK not ready, skipping server event:', eventName)
      return
    }

    const purchaseContext = buildFacebookPurchaseContext({
      eventName,
      eventId,
      userData,
      value,
      currency
    })
    const purchaseDedupKey = purchaseContext.dedupKey

    if (purchaseContext.isDuplicate) {
      console.warn('Facebook Purchase server event already sent, skipping duplicate:', purchaseDedupKey)
      return
    }

    const shopId = resolveFacebookShopId()
    const authorization = getToken() || getMallAppToken() || ''
    const serverUserData = await buildFacebookServerUserData(userData)
    const data = [{
      eventName,
      eventTime: Math.floor(Date.now() / 1000),
      eventId: eventId || generateEventId(),
      eventSourceUrl: buildFacebookEventSourceUrl(),
      actionSource: 'website',
      optOut: false,
      userData: serverUserData,
      customData: buildFacebookServerCustomData(eventName, contents, value, currency, facebookProperties),
      originalEventData: {
        eventTime: Math.floor(Date.now() / 1000),
        eventName
      }
    }]

    if (!data[0].userData) {
      delete data[0].userData
    }

    if (!data[0].customData) {
      delete data[0].customData
    }

    await useNuxtApp().$customFetch('/mall/api/mall/app/facebook/sendEvents', {
      method: 'POST',
      credentials: 'include',
      headers: {
        authorization,
        shopId
      },
      body: {
        param: {
          shopId,
          userId: resolveFacebookUserId(userData || {}),
          data
        }
      }
    })

    if (eventName === 'Purchase') {
      markFacebookPurchaseEventSent(purchaseDedupKey)
    }
  } catch (error) {
    console.warn('Facebook server tracking error:', error)
  }
}

/**
 * Enhanced trackEvent function supporting Google Analytics, TikTok Pixel, and Facebook Pixel
 * @param {string} eventType - Event type (ViewContent, AddToCart, etc.)
 * @param {Object} [options={}] - Event options
 * @param {Array} [options.contents] - Array of content objects for TikTok and Facebook
 * @param {number} [options.value] - Event value
 * @param {string} [options.currency="USD"] - Currency code (ISO 4217)
 * @param {string} [options.search_string] - Search keywords (for Search event)
 * @param {Object} [options.userData] - User identification data for TikTok and Facebook
 * @param {string} [options.eventId] - Event ID shared with pixel/server-side tracking
 * @param {string} [options.orderCode] - Required SO-prefixed business order code for Purchase
 * @param {string} [options.orderId] - Internal order identifier used only by blocked-event diagnostics
 * @param {string} [options.payOrderCode] - Payment identifier used only by blocked-event diagnostics
 * @param {Object} [options.orderContext] - Full order context retained by blocked-event diagnostics
 * @param {string|string[]} [options.googleAdsPurchaseSendTo] - Dynamic Google Ads purchase conversion destinations
 * @param {string} [options.transactionId] - Stable transaction ID used for Google Ads deduplication
 * @param {string} [options.category] - GA category (for backward compatibility)
 * @param {string} [options.action] - GA action (for backward compatibility)
 * @param {string} [options.label] - GA label (for backward compatibility)
 */
export function trackEvent(eventType, options = {}) {
  const {
    contents = [],
    value,
    currency = "USD",
    search_string,
    userData,
    eventId,
    tikTokProperties = {},
    facebookProperties = {},
    orderCode,
    orderId,
    payOrderCode,
    orderContext,
    googleAdsPurchaseSendTo,
    transactionId,
    // Backward compatibility for Google Analytics
    category,
    action,
    label
  } = options;

  // Keep zero-value/invalid orders out of all Purchase conversion channels.
  // The success page records zero-value orders separately in clientEventLog.
  if (eventType === 'Purchase' && (!Number.isFinite(Number(value)) || Number(value) <= 0)) {
    return { eventId: undefined, skipped: true, reason: 'non_positive_purchase_amount' };
  }

  // Purchase conversions are valid only after the business SO order code is
  // available. Record rejected attempts internally, without sending TikTok,
  // Meta/server-side Facebook, or GA purchase events.
  if (eventType === 'Purchase' && !isValidOrderSuccessCode(orderCode)) {
    reportWebOrderSuccessBlocked({
      ...(orderContext || {}),
      orderCode,
      orderId,
      payOrderCode,
      amount: value,
      currency,
      items: orderContext?.items || contents,
      source: orderContext?.source || 'analytics_purchase_guard',
    });
    return { eventId: undefined, skipped: true, reason: 'order_code_not_so_prefix' };
  }

  const ownedEventName = {
    ViewContent: 'view_content',
    AddToCart: 'add_to_cart',
    InitiateCheckout: 'initiate_checkout',
    CompleteRegistration: 'registration_success',
  }[eventType];
  const sharedEventId = ownedEventName ? (eventId || generateEventId('web')) : eventId;
  if (ownedEventName) {
    reportWebFunnelEvent(ownedEventName, {
      eventId: sharedEventId,
      contents,
      value,
      currency: ownedEventName === 'registration_success' ? undefined : currency,
      itemCount: contents.length || undefined,
    });
  }

  // Handle user identification for TikTok and Facebook
  if (userData && Object.keys(userData).length > 0) {
    identifyTikTokUser(userData);
    identifyFacebookUser(userData);
  }

  // TikTok event mapping
  const tikTokEvents = {
    'ViewContent': 'ViewContent',
    'AddToWishlist': 'AddToWishlist',
    'Search': 'Search',
    'AddPaymentInfo': 'AddPaymentInfo',
    'AddToCart': 'AddToCart',
    'InitiateCheckout': 'InitiateCheckout',
    'PlaceAnOrder': 'PlaceAnOrder',
    'CompleteRegistration': 'CompleteRegistration',
    'Purchase': 'Purchase'
  };

  // Facebook event mapping
  const facebookEvents = {
    'ViewContent': 'ViewContent',
    'AddToWishlist': 'AddToWishlist',
    'Search': 'Search',
    'AddPaymentInfo': 'AddPaymentInfo',
    'AddToCart': 'AddToCart',
    'InitiateCheckout': 'InitiateCheckout',
    'CompleteRegistration': 'CompleteRegistration',
    'Purchase': 'Purchase', 
    'Lead': 'Lead',
    'Contact': 'Contact',
    'CustomizeProduct': 'CustomizeProduct',
    'Donate': 'Donate',
    'FindLocation': 'FindLocation',
    'Schedule': 'Schedule',
    'StartTrial': 'StartTrial',
    'SubmitApplication': 'SubmitApplication',
    'Subscribe': 'Subscribe'
  };

  const resolvedFacebookEventId = facebookEvents[eventType]
    ? (sharedEventId || generateEventId())
    : sharedEventId;
  const resolvedTikTokEventId = sharedEventId;

  if (tikTokEvents[eventType]) {
    // Build TikTok parameters
    const tikTokParams = {
      contents: contents.map(content => ({
        content_id: content.content_id || content.id,
        content_type: content.content_type || 'product',
        content_name: content.content_name || content.name
      })),
      currency: currency,
      // TikTok requires value to be a number (integer or decimal) without currency symbols, letters, special characters, or commas
      // Value is required, so we always include it after normalization
      value: normalizeValue(value),
      ...normalizePlatformProperties(tikTokProperties)
    };

    // Add search_string for Search event
    if (eventType === 'Search' && search_string) {
      tikTokParams.search_string = search_string;
    }

    trackTikTokEvent(tikTokEvents[eventType], tikTokParams, {
      eventId: resolvedTikTokEventId
    });
  }

  if (facebookEvents[eventType]) {
    // Build Facebook parameters
    const facebookParams = {
      content_ids: contents.map(content => content.content_id || content.id),
      content_name: contents.length > 0 ? (contents[0].content_name || contents[0].name) : undefined,
      content_type: contents.length > 0 ? (contents[0].content_type || 'product') : 'product',
      currency: currency,
      // Normalize value to ensure it's a valid number
      value: normalizeValue(value),
      ...normalizePlatformProperties(facebookProperties)
    };

    // Add search_string for Search event
    if (eventType === 'Search' && search_string) {
      facebookParams.search_string = search_string;
    }

    // Remove undefined values
    Object.keys(facebookParams).forEach(key => {
      if (facebookParams[key] === undefined) {
        delete facebookParams[key];
      }
    });

    trackFacebookEvent(eventType, facebookParams, {
      eventId: resolvedFacebookEventId
    });

    void sendFacebookServerEvent(facebookEvents[eventType], {
      eventId: resolvedFacebookEventId,
      contents,
      value,
      currency,
      userData,
      facebookProperties
    })
  }

  // Google Ads is intentionally isolated from the TikTok and Meta payloads above.
  // Funnel and retail remarketing events route only to configured AW destinations.
  trackGoogleAdsMarketingEvent(eventType, {
    contents,
    value,
    currency,
    searchString: search_string,
    transactionId,
  })

  // Backward compatibility for GA destinations only; AW destinations use the
  // dedicated Google Ads event path above to avoid duplicate Ads events.
  if (category && action) {
    trackGoogleAnalytics(category, action, label, value);
  }

  if (eventType === 'Purchase') {
    trackGoogleAdsConversion(googleAdsPurchaseSendTo, {
      value,
      currency,
      transactionId,
    })
  }

  return {
    eventId: resolvedFacebookEventId || resolvedTikTokEventId
  };
}
/*

// Universal events (work with TikTok Pixel, Facebook Pixel, and Google Analytics)
trackEvent('ViewContent', {
  contents: [{ content_id: '123', content_name: 'Product Name' }],
  value: 99.99,
  currency: 'USD'
});

// AddToCart with user identification (supports both TikTok and Facebook)
trackEvent('AddToCart', {
  contents: [{ content_id: '123', content_name: 'Product Name' }],
  value: 99.99,
  currency: 'USD',
  userData: {
    email: 'hashed_email@example.com', // SHA-256 hashed
    phone_number: 'hashed_phone@example.com', // SHA-256 hashed (optional)
    first_name: 'hashed_first_name', // SHA-256 hashed (Facebook only)
    last_name: 'hashed_last_name' // SHA-256 hashed (Facebook only)
  }
});

// Search event
trackEvent('Search', {
  contents: [{ content_id: 'search_results', content_name: 'Search Results' }],
  value: 0,
  currency: 'USD',
  search_string: 'red shoes'
});

// Purchase event
trackEvent('Purchase', {
  contents: [
    { content_id: '123', content_name: 'Product A', content_type: 'product' },
    { content_id: '456', content_name: 'Product B', content_type: 'product' }
  ],
  value: 199.99,
  currency: 'USD',
  orderCode: 'SO123456789'
});

// Lead/Contact event
trackEvent('Lead', {
  contents: [{ content_id: 'contact_form', content_name: 'Contact Form' }],
  value: 0,
  currency: 'USD'
});

// Backward compatibility with Google Analytics
trackEvent('ecommerce', {
  category: 'ecommerce',
  action: 'purchase',
  label: 'Product Purchase',
  value: 199.99
});
*/
